import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Calculator, Loader2, Sparkles, BarChart3 } from 'lucide-react';

export default function BidEstimationPage() {
  const { api } = useAuth();
  const [form, setForm] = useState({ description: '', shot_count: '', complexity: 'Medium', deadline: '', style: '2D Animation' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const estimate = async () => {
    setLoading(true);
    try {
      const res = await api.post('/ai/estimate-bid', form);
      setResult(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="bid-estimation-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-white">AI Bid Estimation</h1>
        <p className="text-xs text-zinc-500 mt-1">Generate intelligent bid estimates based on historical studio data</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
          <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider mb-4">Project Details</h3>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] text-zinc-500 uppercase">Project Description</label>
              <textarea data-testid="bid-description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} placeholder="Describe the project scope, style, and deliverables..." className="w-full bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder:text-zinc-600" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Shot Count</label>
                <input data-testid="bid-shot-count" type="number" value={form.shot_count} onChange={e => setForm({...form, shot_count: e.target.value})} placeholder="e.g., 200" className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white px-3 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Complexity</label>
                <select value={form.complexity} onChange={e => setForm({...form, complexity: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-2 focus:outline-none">
                  {['Low', 'Medium', 'High', 'Very High', 'Extreme'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Style</label>
                <select value={form.style} onChange={e => setForm({...form, style: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-2 focus:outline-none">
                  {['2D Animation', '3D Animation', 'Mixed 2D/3D', 'Stop Motion', 'VFX Heavy'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Deadline</label>
                <input type="text" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} placeholder="e.g., 6 months" className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white px-3 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
            </div>
            <button data-testid="generate-bid-btn" onClick={estimate} disabled={loading || !form.description} className="w-full h-9 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? 'Generating Estimate...' : 'Generate AI Bid Estimate'}
            </button>
          </div>

          {result?.historical_avg_rates && (
            <div className="mt-4 pt-4 border-t border-zinc-800">
              <h4 className="text-[10px] text-zinc-500 uppercase mb-2">Historical Avg Rates (from Studio Data)</h4>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(result.historical_avg_rates).map(([k, v]) => (
                  <div key={k} className="bg-zinc-800/50 rounded-sm p-2 text-center">
                    <p className="text-[9px] text-zinc-500 capitalize">{k}</p>
                    <p className="text-xs font-mono text-emerald-400">{v?.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Result */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Calculator className="w-4 h-4 text-blue-400" />
            <h3 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">AI Estimate Result</h3>
          </div>
          {result ? (
            <div className="prose prose-invert prose-xs max-w-none" data-testid="bid-result">
              <pre className="text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed bg-zinc-950 border border-zinc-800 rounded-sm p-4 overflow-y-auto max-h-[55vh]">{result.estimate}</pre>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <BarChart3 className="w-12 h-12 text-zinc-800 mb-3" />
              <p className="text-xs text-zinc-600">Fill in project details and click "Generate" to get an AI-powered bid estimate based on your studio's historical data.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
